import {
  $api,
  $apiAuth,
  ACCESS_TOKEN_KEY,
  setAuthHeaderFromToken,
} from "@/shared/api/api";
import {
  AuthResponse,
  EmailAuthRequest,
  EmailAuthResponse,
  IRegister,
  RefreshTokenResponse,
} from "@/shared/types/auth";

// Query Functions для TanStack Query
export default class AuthService {
  private static readonly REFRESH_TOKEN_KEY = "refresh_token";

  static initAuthFromStorage() {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const token = window.localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token) {
        setAuthHeaderFromToken(token);
      }
    } catch {
      // ignore
    }
  }

  static setTokens(tokens: EmailAuthResponse) {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      window.localStorage.setItem(
        AuthService.REFRESH_TOKEN_KEY,
        tokens.refresh_token,
      );
    } catch {
      // ignore
    }

    setAuthHeaderFromToken(tokens.access_token);
  }

  static setAccessToken(access: string) {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(ACCESS_TOKEN_KEY, access);
    } catch {
      // ignore
    }

    setAuthHeaderFromToken(access);
  }

  static clearTokens() {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(ACCESS_TOKEN_KEY);
        window.localStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
      } catch {
        // ignore
      }
    }

    setAuthHeaderFromToken(null);
  }

  // Логин пользователя (для mutation)
  // Использует /web/email_auth/ согласно новой документации
  static async login(data: EmailAuthRequest): Promise<EmailAuthResponse> {
    const response = await $apiAuth.post<EmailAuthResponse>(
      `/web/email_auth/`,
      data,
    );

    const tokens = response.data;
    AuthService.setTokens(tokens);

    return tokens;
  }

  // Регистрация пользователя (для mutation)
  // Использует /web/user/ согласно новой документации
  static async register(data: IRegister): Promise<AuthResponse> {
    const response = await $apiAuth.post<AuthResponse>(`/web/user/`, data);
    return response.data;
  }

  // Обновление access-токена по refresh
  static async refreshToken(): Promise<string> {
    if (typeof window === "undefined") {
      throw new Error("refreshToken is not available in SSR");
    }

    const refresh = window.localStorage.getItem(AuthService.REFRESH_TOKEN_KEY);
    if (!refresh) {
      throw new Error("No refresh token available");
    }

    const response = await $api.post<RefreshTokenResponse>(
      "/token/refresh/",
      { refresh },
      { __isRefreshRequest: true } as Parameters<typeof $api.post>[2]
    );

    const { access } = response.data;
    AuthService.setAccessToken(access);
    return access;
  }

  // Проверка текущего пользователя (для query)
  // Использует /web/user/me/ согласно новой документации
  static async checkAuth(): Promise<AuthResponse> {
    const response = await $api.get<AuthResponse>(`/web/user/me/`);
    return response.data;
  }

  // Логаут пользователя (для mutation)
  // В новой документации нет явного эндпоинта для логаута, оставляем как есть
  static async logout(): Promise<void> {
    try {
      await $api.post(`/auth/logout`);
    } finally {
      AuthService.clearTokens();
    }
  }
}
